import * as React from 'react';

import { CalendarDate, DateValue } from '@internationalized/date';
import {
  enumLoadSortOption,
  enumPurchaseLoadSortOption,
  LoadSortOption,
  LoadStatus,
  PurchaseLoadSortOption,
  PurchaseLoadStatus,
} from '@rematter/frontend-lib-api-client';
import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { facilitiesQueryAtom } from '../_graphql/queryFacilities';
import { loadDetailQueryAtom } from '../_graphql/queryLoadDetail';
import { loadPhotosQueryAtom } from '../_graphql/queryLoadPhotos';
import { purchaseLoadDetailQueryAtom } from '../_graphql/queryPurchaseLoadDetail';
import { purchaseLoadPhotosQueryAtom } from '../_graphql/queryPurchaseLoadPhotos';
import { transactionsInboundQueryAtom } from '../_graphql/queryTransactionsInbound';
import { transactionsOutboundQueryAtom } from '../_graphql/queryTransactionsOutbound';
import { TransactionDirection } from '../types/transaction';

import { filesWithUrlsAtom } from './files.state';

import { $location, atomFromQueryString } from '~/shared/state';
import {
  dateToZonedDateTimeEnd,
  dateToZonedDateTimeStart,
} from '~/shared/utils/dateTime';

// 🔘🔘🔘 PLAIN ATOMS
export const $direction = atomWithStorage<TransactionDirection>(
  'transactions.direction',
  'inbound',
);
export const $inboundSorting = atom<PurchaseLoadSortOption[]>([
  enumPurchaseLoadSortOption.CreatedAtDesc,
]);
export const $outboundSorting = atom<LoadSortOption[]>([
  enumLoadSortOption.CreatedAtDesc,
]);
export const $selectedInboundKeys = atom<DataGridSelection>(
  new Set<React.Key>(),
);
export const $selectedOutboundKeys = atom<DataGridSelection>(
  new Set<React.Key>(),
);
export const $selectedTransactionId = atom<string | null>(null);
export const $selectedOutboundTransactionId = atom<string | null>(null);

// 🟣🟣🟣 PARAMS (URL-synced)
export const $searchTerm = atomFromQueryString.string('q');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);

// 🟣🟣🟣 COLUMN FILTER PARAMS
export const $columnSelectedInboundStatuses =
  atomFromQueryString.stringSet<PurchaseLoadStatus>('colStatuses');
export const $columnSelectedOutboundStatuses =
  atomFromQueryString.stringSet<LoadStatus>('colOutStatuses');
export const $columnSelectedSupplierIds =
  atomFromQueryString.stringSet('colSuppliers');
export const $columnSelectedFacilityIds =
  atomFromQueryString.stringSet('colFacilities');
export const $columnReceivedDateRange = atomFromQueryString.dateRange();
export const $columnSelectedOutboundCustomerIds =
  atomFromQueryString.stringSet('colOutCustomers');
export const $columnSelectedOutboundFacilityIds =
  atomFromQueryString.stringSet('colOutFacilities');
export const $columnShippedDateRange = (() => {
  type DateRange = { start: DateValue; end: DateValue };

  const parseCalendarDateFromString = (
    dateString: string,
  ): CalendarDate | null => {
    try {
      const [year, month, day] = dateString.split('-').map(Number);
      if (!year || !month || !day) return null;
      return new CalendarDate(year, month, day);
    } catch {
      return null;
    }
  };

  const formatCalendarDateToString = (date: DateValue): string => {
    if ('year' in date && 'month' in date && 'day' in date) {
      const year = date.year.toString().padStart(4, '0');
      const month = date.month.toString().padStart(2, '0');
      const day = date.day.toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  };

  return atom(
    (get): DateRange | null => {
      const location = get($location);
      const fromParam = location.searchParams?.get('outFrom') ?? '';
      const toParam = location.searchParams?.get('outTo') ?? '';

      if (!fromParam && !toParam) return null;

      const start = fromParam ? parseCalendarDateFromString(fromParam) : null;
      const end = toParam ? parseCalendarDateFromString(toParam) : null;

      if (start && !end) return { start, end: start };
      if (end && !start) return { start: end, end };
      if (start && end) return { start, end };

      return null;
    },
    (get, set, value: DateRange | null) => {
      const location = get($location);
      const newSearchParams = new URLSearchParams(location.searchParams);

      if (!value) {
        newSearchParams.delete('outFrom');
        newSearchParams.delete('outTo');
      } else {
        if (value.start) {
          newSearchParams.set(
            'outFrom',
            formatCalendarDateToString(value.start),
          );
        } else {
          newSearchParams.delete('outFrom');
        }
        if (value.end) {
          newSearchParams.set('outTo', formatCalendarDateToString(value.end));
        } else {
          newSearchParams.delete('outTo');
        }
      }

      set($location, {
        pathname: location.pathname,
        searchParams: newSearchParams,
        ...(location.hash ? { hash: location.hash } : {}),
      });
    },
  );
})();

// 🔵🔵🔵 LOCAL STATE / HELPER
export const $facilityFilterSearchTerm = atom<string | undefined>(undefined);

// 🟢🟢🟢 COMPUTED DATA -> filters active
export const $filtersActive = atom((get) => {
  const searchTerm = get($searchTerm);
  const columnStatuses = get($columnSelectedInboundStatuses);
  const columnOutboundStatuses = get($columnSelectedOutboundStatuses);
  const columnSupplierIds = get($columnSelectedSupplierIds);
  const columnFacilityIds = get($columnSelectedFacilityIds);
  const columnDateRange = get($columnReceivedDateRange);
  const columnOutboundCustomerIds = get($columnSelectedOutboundCustomerIds);
  const columnOutboundFacilityIds = get($columnSelectedOutboundFacilityIds);
  const columnShippedDateRange = get($columnShippedDateRange);
  return (
    (searchTerm !== undefined && searchTerm?.trim() !== '') ||
    columnStatuses.size > 0 ||
    columnOutboundStatuses.size > 0 ||
    columnSupplierIds.size > 0 ||
    columnFacilityIds.size > 0 ||
    columnDateRange !== null ||
    columnOutboundCustomerIds.size > 0 ||
    columnOutboundFacilityIds.size > 0 ||
    columnShippedDateRange !== null
  );
});

// 🟢🟢🟢 COMPUTED DATA -> total counts
export const $inboundTotal = atom((get) => {
  const { data } = get($remoteInbound);
  return data?.totalCount ?? 0;
});

export const $outboundTotal = atom((get) => {
  const { data } = get($remoteOutbound);
  return data?.total ?? 0;
});

// 🟢🟢🟢 REMOTE DATA -> facility filter options
export const $remoteFacilityFilterOptions = facilitiesQueryAtom((get) => {
  const searchTerm = get($facilityFilterSearchTerm);
  return {
    variables: {
      searchTerm,
      limit: 20,
    },
  };
});

// 🟢🟢🟢 REMOTE DATA -> inbound transactions
export const $remoteInbound = transactionsInboundQueryAtom((get) => {
  const direction = get($direction);
  const searchTerm = direction === 'inbound' ? get($searchTerm) : undefined;
  const limit = get($limit);
  const offset = get($offset);
  const sorting = get($inboundSorting);
  const columnStatuses = get($columnSelectedInboundStatuses);
  const columnSupplierIds = get($columnSelectedSupplierIds);
  const columnFacilityIds = get($columnSelectedFacilityIds);
  const columnDateRange = get($columnReceivedDateRange);

  const params: {
    searchTerm?: string;
    status?: PurchaseLoadStatus[];
    supplierAccountIds?: string[];
    facilityIds?: string[];
    fromReceivedDate?: string;
    toReceivedDate?: string;
    limit: number;
    offset: number;
    sorting: PurchaseLoadSortOption[];
  } = {
    limit,
    offset,
    sorting,
  };

  if (searchTerm?.trim()) params.searchTerm = searchTerm;
  if (columnStatuses.size > 0) {
    params.status = Array.from(columnStatuses) as PurchaseLoadStatus[];
  }
  if (columnSupplierIds.size > 0) {
    params.supplierAccountIds = Array.from(columnSupplierIds);
  }
  if (columnFacilityIds.size > 0) {
    params.facilityIds = Array.from(columnFacilityIds);
  }
  if (columnDateRange?.start) {
    params.fromReceivedDate = dateToZonedDateTimeStart(columnDateRange.start);
  }
  if (columnDateRange?.end) {
    params.toReceivedDate = dateToZonedDateTimeEnd(columnDateRange.end);
  }

  return {
    variables: params,
  };
});

// 🟢🟢🟢 REMOTE DATA -> outbound transactions
export const $remoteOutbound = transactionsOutboundQueryAtom((get) => {
  const direction = get($direction);
  const searchTerm = direction === 'outbound' ? get($searchTerm) : undefined;
  const limit = get($limit);
  const offset = get($offset);
  const sorting = get($outboundSorting);
  const columnStatuses = get($columnSelectedOutboundStatuses);
  const columnCustomerIds = get($columnSelectedOutboundCustomerIds);
  const columnFacilityIds = get($columnSelectedOutboundFacilityIds);
  const columnShippedDateRange = get($columnShippedDateRange);

  const params: {
    searchTerm?: string;
    status?: LoadStatus[];
    columnStatuses?: LoadStatus[];
    accountIds?: string[];
    facilityIds?: string[];
    fromShippedDate?: string;
    toShippedDate?: string;
    limit: number;
    offset: number;
    sorting: LoadSortOption[];
  } = {
    limit,
    offset,
    sorting,
  };

  if (searchTerm?.trim()) params.searchTerm = searchTerm;
  if (columnStatuses.size > 0) {
    params.columnStatuses = Array.from(columnStatuses) as LoadStatus[];
  }
  if (columnCustomerIds.size > 0) {
    params.accountIds = Array.from(columnCustomerIds);
  }
  if (columnFacilityIds.size > 0) {
    params.facilityIds = Array.from(columnFacilityIds);
  }
  if (columnShippedDateRange?.start) {
    params.fromShippedDate = columnShippedDateRange.start.toString();
  }
  if (columnShippedDateRange?.end) {
    params.toShippedDate = columnShippedDateRange.end.toString();
  }

  return {
    variables: params,
  };
});

// 🟢🟢🟢 REMOTE DATA -> selected transaction detail
export const $selectedTransactionDetail = purchaseLoadDetailQueryAtom((get) => {
  const id = get($selectedTransactionId);
  return {
    variables: { id: id ?? '' },
    skip: !id,
  };
});

// 🟢🟢🟢 REMOTE DATA -> selected transaction photos (without URLs)
const $selectedTransactionPhotosRaw = purchaseLoadPhotosQueryAtom((get) => ({
  variables: { id: get($selectedTransactionId) ?? '' },
  lazy: true,
}));

// 🟢🟢🟢 REMOTE DATA -> selected transaction photos (with URLs)
export const $selectedTransactionPhotos = filesWithUrlsAtom((get) => ({
  variables: { id: get($selectedTransactionId) ?? '' },
  skip: !get($selectedTransactionId),
  photosAtom: $selectedTransactionPhotosRaw,
}));

// 🟢🟢🟢 REMOTE DATA -> selected outbound transaction detail
export const $selectedOutboundTransactionDetail = loadDetailQueryAtom((get) => {
  const id = get($selectedOutboundTransactionId);
  return {
    variables: { id: id ?? '' },
    skip: !id,
  };
});

// 🟢🟢🟢 REMOTE DATA -> selected outbound transaction photos (without URLs)
const $selectedOutboundPhotosRaw = loadPhotosQueryAtom((get) => ({
  variables: { id: get($selectedOutboundTransactionId) ?? '' },
  lazy: true,
}));

// 🟢🟢🟢 REMOTE DATA -> selected outbound transaction photos (with URLs)
export const $selectedOutboundTransactionPhotos = filesWithUrlsAtom((get) => ({
  variables: { id: get($selectedOutboundTransactionId) ?? '' },
  skip: !get($selectedOutboundTransactionId),
  photosAtom: $selectedOutboundPhotosRaw,
}));
